// apps/admin/src/features/billing/InvoicePdfPreview.tsx
//
// Phase 8.2 Wave 3 Task 12 — Invoice Detail A2 PDF preview iframe.
//
// vg-binding: CONTEXT:decisions=P8.D-485,P8.D-499
// vg-binding: PHASE:08.2-admin-finance-catalog
//
//  A2: Invoice Detail page shows a PDF preview alongside the
// line items table. Per P4.1 API contract `invoice.pdf_url` is `null` while
// status === 'DRAFT' (PDF only rendered after SENT). This component
// gracefully degrades to an empty-state placeholder when no URL is
// available so the layout slot stays intact during loading / draft states.

import type { JSX } from 'react';
import { useTranslation } from '../../i18n/useTranslation.js';

export interface InvoicePdfPreviewProps {
  /** Signed URL to the invoice PDF (nullable — DRAFT invoices have no PDF). */
  pdfUrl: string | null | undefined;
  /** Invoice number for accessible iframe title. */
  invoiceNumber?: string | null;
  /** Optional Tailwind class overrides on the outer frame. */
  className?: string;
}

/**
 * Renders the invoice PDF inside an `<iframe>` so admins can verify the
 * exact document before issuing CN/DN or marking paid. Falls back to a
 * dashed placeholder when `pdfUrl` is missing — common for DRAFT
 * invoices where the PDF has not been rendered yet (BE renders on the
 * DRAFT → SENT transition).
 */
export function InvoicePdfPreview(props: InvoicePdfPreviewProps): JSX.Element {
  const { pdfUrl, invoiceNumber, className } = props;
  const { t } = useTranslation('finance');

  const wrapClass = [
    'flex h-full min-h-[480px] w-full flex-col overflow-hidden rounded-md border border-pw-border bg-pw-bg',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  if (!pdfUrl) {
    return (
      <div
        className={wrapClass}
        data-testid="invoice-pdf-preview-empty"
      >
        <div className="flex flex-1 flex-col items-center justify-center gap-2 border border-dashed border-pw-border px-6 py-12 text-center text-sm text-pw-text-muted">
          <span aria-hidden="true" className="text-2xl">
            ▢
          </span>
          <p>{t('invoice.pdfPreview.unavailable')}</p>
          <p className="text-xs">
            {t('invoice.pdfPreview.draftHint')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={wrapClass}>
      <div className="flex items-center justify-between border-b border-pw-border bg-pw-surface px-3 py-1.5 text-xs">
        <span className="font-medium text-pw-text-muted">
          {t('invoice.pdfPreview.label')}
        </span>
        <a
          href={pdfUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="font-medium text-pw-primary-strong hover:underline"
          data-testid="invoice-pdf-preview-open-btn"
        >
          {t('invoice.pdfPreview.openNewTab')}
        </a>
      </div>
      <iframe
        src={pdfUrl}
        title={t('invoice.pdfPreview.iframeTitle', { number: invoiceNumber ?? '' })}
        className="h-full min-h-[480px] w-full flex-1 bg-white" /* dark-ok: PDF iframe renders a white paper document */
        data-testid="invoice-pdf-preview-iframe"
      />
    </div>
  );
}

export default InvoicePdfPreview;
